import { InteracWire } from '@src/types/interac'
import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

export const SELECTED_TRANSFER_PARAM = 'selected'

export interface InteracQueueCursor {
  transfer: InteracWire | undefined
  index: number
  isActive: (index: number) => boolean
  itemId: (index: number) => string
  activeDescendant: string | undefined
  moveTo: (index: number) => void
  move: (delta: number) => boolean
  advancePast: (index: number) => void
}

export const useInteracQueueCursor = (queue: InteracWire[], idPrefix: string): InteracQueueCursor => {
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedId = searchParams.get(SELECTED_TRANSFER_PARAM)

  const index = useMemo(() => {
    const found = queue.findIndex((wire) => wire.id === selectedId)
    return found >= 0 ? found : 0
  }, [queue, selectedId])

  const select = useCallback(
    (wire: InteracWire | undefined, replace: boolean) => {
      if (!wire) return
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous)
          next.set(SELECTED_TRANSFER_PARAM, wire.id)
          return next
        },
        { replace },
      )
    },
    [setSearchParams],
  )

  const moveTo = useCallback(
    (target: number) => {
      if (target === index) return
      select(queue[target], false)
    },
    [index, queue, select],
  )

  const advancePast = useCallback(
    (cleared: number) => {
      select(queue[cleared + 1] ?? queue[cleared - 1], true)
    },
    [queue, select],
  )

  const move = useCallback(
    (delta: number) => {
      const target = index + delta
      if (target < 0 || target >= queue.length) return false
      moveTo(target)
      return true
    },
    [index, moveTo, queue.length],
  )

  const itemId = useCallback((row: number) => `${idPrefix}-${row}`, [idPrefix])

  return useMemo(
    () => ({
      transfer: queue[index],
      index,
      isActive: (row: number) => row === index,
      itemId,
      activeDescendant: queue.length > 0 ? itemId(index) : undefined,
      moveTo,
      move,
      advancePast,
    }),
    [advancePast, index, itemId, move, moveTo, queue],
  )
}
